import { AreaUnit, LengthUnit, WeightUnit } from "@services/demosService";

const LB_TO_KG = 0.45359237;
// A US (short) ton is a fixed 2000 lb, unlike the crop-dependent bushel
const POUNDS_PER_SHORT_TON = 2000;

// Conversion options
export type Options = {
  // Number of pounds per bushel
  poundsPerBushel?: number,
  // Number of pounds per bag
  poundsPerBag?: number,
};

// defaultOptions is a default options for the conversion
const defaultOptions: Options = {
  poundsPerBushel: 60,
  poundsPerBag: 100,
};

/**
 * Standard bushel weight per grain crop, in pounds.
 *
 * A bushel is a legally DEFINED weight that differs by crop — a bushel of corn
 * is 56 lb, a bushel of wheat is 60 lb. It is the fixed divisor the elevator
 * settles on, so it is what belongs in `poundsPerBushel`.
 *
 * It is NOT test weight. Test weight is the measured density of one particular
 * load (graded off a test-weight cup, and also quoted in lb/bu), and it is a
 * quality grade only: wheat that tests 61 lb still sells in 60 lb bushels.
 * Users read the test weight off their elevator ticket and try to type it here,
 * which silently skews every stored mass — hence this table and the picker in
 * SettingsScreen, so the common crops need no typing at all.
 *
 * `cropId` matches the ids seeded in backend-ai/src/db/seed.ts; `langKey` is a
 * key under `crops` in the localization files.
 */
export type BushelCrop = {
  cropId: string;
  langKey: string;
  poundsPerBushel: number;
};

export const BUSHEL_CROPS: BushelCrop[] = [
  { cropId: "crop-corn", langKey: "cornName", poundsPerBushel: 56 },
  { cropId: "crop-soybean", langKey: "soybeanName", poundsPerBushel: 60 },
  { cropId: "crop-wheat", langKey: "wheatName", poundsPerBushel: 60 },
  { cropId: "crop-barley", langKey: "barleyName", poundsPerBushel: 48 },
  { cropId: "crop-oats", langKey: "oatsName", poundsPerBushel: 32 },
  { cropId: "crop-rye", langKey: "ryeName", poundsPerBushel: 56 },
  { cropId: "crop-rice", langKey: "riceName", poundsPerBushel: 45 },
  { cropId: "crop-peas", langKey: "peasName", poundsPerBushel: 60 },
  { cropId: "crop-rapeseed", langKey: "rapeseedName", poundsPerBushel: 50 },
  { cropId: "crop-sunflower", langKey: "sunflowerName", poundsPerBushel: 24 },
];

/** Standard bushel weight for a crop id, or null when the crop has no standard. */
export function bushelWeightForCrop(cropId?: string | null): number | null {
  if (!cropId) return null;
  return BUSHEL_CROPS.find((c) => c.cropId === cropId)?.poundsPerBushel ?? null;
}

/**
 * Returns a map of WeightUnit to its value in kilograms.
 * This is a function because it depends on the dynamic options.
 */
function getWeightFactors(
  options: Options = defaultOptions,
): Record<WeightUnit, number> {
  const finalOptions = { ...defaultOptions, ...options };
  return {
    [WeightUnit.Kilogram]: 1,
    [WeightUnit.Ton]: 1000,
    [WeightUnit.Centner]: 100,
    [WeightUnit.Pound]: LB_TO_KG,
    [WeightUnit.ShortTon]: POUNDS_PER_SHORT_TON * LB_TO_KG,
    [WeightUnit.Bushel]: finalOptions.poundsPerBushel! * LB_TO_KG,
    [WeightUnit.Bag]: finalOptions.poundsPerBag! * LB_TO_KG,
  };
}

/** Square meters per area unit */
const SQR_M = 1;

/**
 * A map of AreaUnit to its value in square meters.
 */
const areaFactors: Record<string, number> = {
  sqr_m: SQR_M,
  [AreaUnit.Hectare]: 10000,
  [AreaUnit.Acre]: 4046.8564224,
};

/**
 * Converts yield from centner/hectare to specified weight and area units
 *
 * @param {number} value - Yield value in centner/hectare
 * @param {WeightUnit} weight - Target weight unit
 * @param {AreaUnit} area - Target area unit
 * @param {Options} options - Optional parameters for bushel and bag conversions
 *
 * @returns {number} Converted yield value
 */
export function convertYield(
  value: number,
  weight: WeightUnit,
  area: AreaUnit,
  options: Options = defaultOptions,
): number {
  if (weight === WeightUnit.Centner && area === AreaUnit.Hectare) {
    return value;
  }

  const toKgFactors = getWeightFactors(options);

  const valueInKgPerSqrM = value / 100;
  const targetWeightFactor = toKgFactors[weight];
  const targetAreaFactor = areaFactors[area];

  const result = (valueInKgPerSqrM * targetAreaFactor) / targetWeightFactor;

  return result;
}

/**
 * Converts a weight value from one unit to another.
 *
 * @param {number} value - The numeric value to convert.
 * @param {WeightUnit} from - The starting weight unit.
 * @param {WeightUnit} to - The target weight unit.
 * @param {Options} options - Optional parameters for bushel and bag conversions.
 *
 * @returns {number} The converted weight value.
 */
export function convertWeight(
  value: number,
  from: WeightUnit,
  to: WeightUnit,
  options: Options = defaultOptions,
): number {
  if (from === to) {
    return value;
  }
  const factors = getWeightFactors(options);
  const valueInKg = value * factors[from];
  return valueInKg / factors[to];
}

/** Metres per length unit. */
/**
 * Speed units. Like length, never a preference of its own: it follows the area
 * unit, so an acre user (feet) reads mph and everyone else km/h. The API and
 * every stored value stay in km/h.
 */
export enum SpeedUnit {
  KmPerHour = "km/h",
  MilePerHour = "mph",
}

const KM_PER_MILE = 1.609344;

export const speedUnitFor = (length: LengthUnit): SpeedUnit =>
  length === LengthUnit.Foot ? SpeedUnit.MilePerHour : SpeedUnit.KmPerHour;

export function convertSpeed(value: number, from: SpeedUnit, to: SpeedUnit): number {
  if (from === to) return value;
  return from === SpeedUnit.KmPerHour ? value / KM_PER_MILE : value * KM_PER_MILE;
}

const lengthFactors: Record<string, number> = {
  [LengthUnit.Meter]: 1,
  [LengthUnit.Foot]: 0.3048,
};

/**
 * Converts a length value from one unit to another.
 *
 * Header/reaper widths are stored and computed in metres everywhere in the
 * backend, so this is a display-layer conversion only — always convert back to
 * metres before writing to the API.
 *
 * @param {number} value - The numeric value to convert.
 * @param {LengthUnit} from - The starting length unit.
 * @param {LengthUnit} to - The target length unit.
 *
 * @returns {number} The converted length value.
 */
export function convertLength(
  value: number,
  from: LengthUnit | string,
  to: LengthUnit,
): number {
  if (from === to) {
    return value;
  }

  const fromFactor = lengthFactors[from] ?? 1;
  const toFactor = lengthFactors[to] ?? 1;
  return (value * fromFactor) / toFactor;
}

/**
 * Rounds a length for display in an input. 6.7 m reads as 21.98 ft rather than
 * 21.98031496062992.
 */
export function roundLengthForDisplay(value: number): number {
  return Number(value.toFixed(2));
}

/**
 * Rounds a length that is about to be written to the API. Keeps the value tidy
 * without losing sub-millimetre precision on a ft -> m conversion.
 */
export function roundLengthForStorage(value: number): number {
  return Number(value.toFixed(4));
}

/**
 * Converts an area value from one unit to another.
 *
 * @param {number} value - The numeric value to convert.
 * @param {AreaUnit} from - The starting area unit.
 * @param {AreaUnit} to - The target area unit.
 *
 * @returns {number} The converted area value.
 */
export function convertArea(
  value: number,
  from: AreaUnit | string,
  to: AreaUnit,
): number {
  if (from === to) {
    return value;
  }

  const fromFactor = areaFactors[from] ?? 1;
  const toFactor = areaFactors[to] ?? 1;
  return (value * fromFactor) / toFactor;
}
